import React, { useEffect, useMemo } from 'react';
import BasAccountPicker from './BasAccountPicker';

/**
 * Dynamic form renderer for a template body's declared inputs.
 *
 * Reads `body.inputs[]` from the template (see
 * accounting-backend/migrations/templates/README.md for the v1
 * contract) and renders one form field per declared input. Field
 * type drives the widget:
 *
 *   currency_gross / currency_net / currency / number → number input
 *   integer                                           → integer input
 *   text                                              → text input
 *   date                                              → date input
 *   account_picker                                    → text input
 *                                                       (4-digit account
 *                                                       number) — a
 *                                                       proper picker
 *                                                       backed by the
 *                                                       tenant chart
 *                                                       is a follow-up
 *   select                                            → <select> with the
 *                                                       declared options
 *
 * Props:
 *   template: the full template row (only body.inputs is read)
 *   values: current values keyed by input name
 *   onChange: (name, newValue) => void
 *   disabled: bool
 */
function TemplateForm({ template, values, onChange, disabled, accounts = [] }) {
    const inputs = useMemo(() => template?.body?.inputs || [], [template]);

    // Seed defaults for inputs that have one and aren't yet filled in.
    // We don't call onChange during render — emit them on mount via
    // effect.
    useEffect(() => {
        if (!template) return;
        for (const inp of inputs) {
            if (values[inp.name] === undefined && inp.default !== undefined) {
                onChange(inp.name, inp.default);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template?.template_id]);

    if (!template) return null;
    if (inputs.length === 0) {
        return (
            <div className="bk-template-form bk-template-form--no-inputs">
                Den här mallen behöver inga uppgifter från dig.
            </div>
        );
    }

    return (
        <form className="bk-template-form" onSubmit={(e) => e.preventDefault()}>
            {inputs.map((input) => (
                <FormField
                    key={input.name}
                    input={input}
                    value={values[input.name]}
                    onChange={(v) => onChange(input.name, v)}
                    disabled={disabled}
                    accounts={accounts}
                />
            ))}
        </form>
    );
}

function FormField({ input, value, onChange, disabled, accounts = [] }) {
    const id = `bk-template-input-${input.name}`;
    const common = {
        id,
        name: input.name,
        disabled,
        required: !!input.required,
        'aria-required': !!input.required,
    };

    let widget;
    switch (input.type) {
        case 'currency':
        case 'currency_gross':
        case 'currency_net':
        case 'number':
            widget = (
                <input
                    type="number"
                    step="0.01"
                    {...common}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                    inputMode="decimal"
                />
            );
            break;
        case 'integer':
            widget = (
                <input
                    type="number"
                    step="1"
                    {...common}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    inputMode="numeric"
                />
            );
            break;
        case 'date':
            widget = (
                <input type="date" {...common} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
            );
            break;
        case 'account_picker':
            widget = (
                <BasAccountPicker
                    id={id}
                    value={value ?? ''}
                    onChange={onChange}
                    accounts={accounts}
                    filter={input.filter}
                    required={!!input.required}
                    disabled={!!disabled}
                />
            );
            break;
        case 'select':
            widget = (
                <select {...common} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
                    <option value="" disabled>Välj…</option>
                    {(input.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            );
            break;
        case 'eu_vat_number':
            widget = (
                <input
                    type="text"
                    {...common}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="t.ex. DE123456789"
                    autoCapitalize="characters"
                    spellCheck={false}
                />
            );
            break;
        case 'text':
        default:
            widget = (
                <input type="text" {...common} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
            );
    }

    return (
        <div className="bk-template-form__field">
            <label htmlFor={id} className="bk-template-form__label">
                {input.label}
                {input.required ? <span className="bk-template-form__required" aria-hidden> *</span> : null}
            </label>
            {widget}
            {hintForType(input) ? (
                <small className="bk-template-form__hint">{hintForType(input)}</small>
            ) : null}
        </div>
    );
}

function hintForType(input) {
    if (input.type === 'currency_gross') {
        return 'Belopp inkl. moms' + (vatRateHint(input) ? ` (${vatRateHint(input)})` : '');
    }
    if (input.type === 'currency_net') {
        return 'Belopp exkl. moms' + (vatRateHint(input) ? ` (${vatRateHint(input)})` : '');
    }
    if (input.type === 'account_picker') return 'Sök efter nummer eller namn — t.ex. "1930" eller "företagskonto".';
    if (input.type === 'eu_vat_number') return 'EU-momsregistreringsnummer från annat EU-land (landskod + nummer). SE-nummer accepteras inte — använd inhemsk mall istället.';
    // Inputs that select a VAT rate (typed integer named like
    // 'rate'/'vat_rate'/'momssats') get a one-line plain-Swedish
    // explanation. Heuristic: the input is a numeric type with a
    // name that suggests a VAT-rate role. Templates that want this
    // hint can also set input.help to a more specific string (TODO
    // — extend the schema if needed).
    if ((input.type === 'integer' || input.type === 'number') && /momssats|vat[_-]?rate|^rate$/i.test(input.name || '')) {
        return 'Standardmomsen är 25 %. 12 % gäller för bl.a. livsmedel, hotell, restaurang. 6 % för böcker, persontrafik och kultur.';
    }
    return null;
}

/**
 * One-line Skatteverket-grounded hint for what each VAT rate means.
 * Surfaced beneath currency_gross/currency_net fields when a literal
 * vat_rate is declared on the input.
 */
function vatRateHint(input) {
    if (typeof input.vat_rate !== 'number') return null;
    if (input.vat_rate === 25) return 'standardmoms — varor och tjänster i allmänhet';
    if (input.vat_rate === 12) return 'reducerad moms — livsmedel, hotell, restaurang';
    if (input.vat_rate === 6) return 'reducerad moms — böcker, persontrafik, kultur';
    if (input.vat_rate === 0) return 'momsfritt';
    return null;
}

export default TemplateForm;
